import { FC, useEffect, useRef, useState } from 'react';
import { Maximize, Pause, Play, Settings, Volume2, VolumeX } from 'lucide-react';
import { VideoPlayerProps } from './VideoPlayer.interface';


const VideoPlayer: FC<VideoPlayerProps> = ({ config, isPreview = false }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const controlsTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (config.autoPlay && videoRef.current) {
      videoRef.current.play().catch(e => console.log("Autoplay blocked:", e));
    }
  }, [config.videoSrc, config.autoPlay]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      const total = videoRef.current.duration;
      setProgress((current / total) * 100);
      setDuration(total);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (videoRef.current) {
      const seekTime = (parseFloat(e.target.value) / 100) * videoRef.current.duration;
      videoRef.current.currentTime = seekTime;
      setProgress(parseFloat(e.target.value));
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (containerRef.current) {
      if (!document.fullscreenElement) {
        containerRef.current.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);

    if (controlsTimeoutRef.current) {
      window.clearTimeout(controlsTimeoutRef.current);
    }

    if (isPlaying) {
      controlsTimeoutRef.current = window.setTimeout(() => {
        setShowControls(false);
      }, 2000);
    }
  };

  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);

    return `${mins < 10 ? '0' : ''}${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  if (!config.videoSrc) {
    return (
      <div className="w-full aspect-video bg-slate-900 rounded-lg border-2 border-slate-700 border-dashed flex items-center justify-center text-slate-500">
        <div className="text-center">
          <Settings className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No video selected</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`relative group w-full aspect-video bg-black overflow-hidden shadow-2xl ${isPreview ? 'rounded-lg' : ''}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        src={config.videoSrc}
        className="w-full h-full object-contain"
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => {
          setIsPlaying(false);
          setShowControls(true);
        }}
        loop={config.loop}
        onClick={togglePlay}
      />

      {/* Overlays - Logos */}
      {config.clientLogoSrc && (
        <div 
          className="absolute top-4 left-4 z-10 transition-all duration-300 pointer-events-none"
          style={{ width: `${config.clientLogoSize || 15}%` }}
        >
          <img src={config.clientLogoSrc} alt="Client Logo" className="w-full h-auto drop-shadow-lg" />
        </div>
      )}
      
      {/* Provider logo removed from player overlay as requested */}

      {/* Play Overlay (Big Center Button) */}
      {!isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 z-20 cursor-pointer" onClick={togglePlay}>
          <div 
            className="p-4 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 hover:scale-110 transition-transform duration-200"
            style={{ color: config.primaryColor }}
          >
            <Play size={48} fill={config.primaryColor} />
          </div>
        </div>
      )}

      {/* Controls Bar */}
      <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent px-4 pb-4 pt-12 transition-opacity duration-300 z-30 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
        {/* Progress Bar */}
        <div className="relative h-1 bg-white/20 rounded-full mb-4 cursor-pointer group/seek">
          <div 
            className="absolute top-0 left-0 h-full rounded-full" 
            style={{ backgroundColor: config.primaryColor, width: `${progress}%` }} 
          />
          <input 
            type="range" 
            min="0" 
            max="100" 
            value={progress} 
            onChange={handleSeek}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>

        {/* Buttons Row */}
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center space-x-4">
            <button onClick={togglePlay} className="hover:text-gray-300 transition-colors">
              {isPlaying ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" />}
            </button>
            
            <div className="flex items-center space-x-2 group/vol">
              <button onClick={toggleMute} className="hover:text-gray-300 transition-colors">
                {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
              </button>
            </div>

            <span className="text-xs font-medium text-gray-300 select-none">
              {formatTime(videoRef.current?.currentTime || 0)} / {formatTime(duration)}
            </span>
          </div>

          <div className="flex items-center space-x-4">
            <button onClick={toggleFullscreen} className="hover:text-gray-300 transition-colors">
              <Maximize size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;